import type { Me, Modul, PlattformRecht, Recht, SperrbaresAddon } from '@dxl/shared';
import {
  ArrowLeftRight, BookOpen, Code, FileText, Handshake, LayoutDashboard, Megaphone, Package, Percent, Radio, Receipt, Settings,
  ShoppingBag, ShoppingCart, SlidersHorizontal, Store, Tags, Target, Trophy, UserCog, Users, Wallet, Watch, LayoutTemplate, ChartColumn, CalendarRange, type LucideIcon,
} from 'lucide-react';

export interface NavPunkt {
  id: string;
  titel: string;
  symbol: LucideIcon;
  /** alle Bereiche laufen in der neuen Plattform – die PHP-Middleware wird nicht mehr verlinkt */
  stand: 'neu';
  recht?: Recht;
  /** alternativ genügt dieses Recht (z. B. „Ankauf ansehen“) */
  oderRecht?: Recht;
  /** Plattform: nötiges Recht aus dem Rechte-Level */
  plattformRecht?: PlattformRecht;
  modul?: Modul;
  /** Bereich gehört zu einem Add-on – ohne Freischaltung mit Schloss (Seite zeigt den Weg zu Paket & Abrechnung) */
  addon?: SperrbaresAddon;
  /** von navigationFuer gesetzt: Add-on nicht frei */
  gesperrt?: boolean;
}

export interface NavGruppe {
  titel?: string;
  /** Überschrift ist selbst ein Link auf die Übersicht des Bereichs (z. B. Retail Media) */
  uebersicht?: { id: string; recht?: Recht; modul?: Modul };
  punkte: NavPunkt[];
}

const PLATTFORM: NavGruppe[] = [
  {
    titel: 'Plattform',
    punkte: [
      { id: 'plattform', titel: 'Übersicht', symbol: LayoutDashboard, stand: 'neu', plattformRecht: 'marktplaetze_lesen' },
      { id: 'marktplaetze', titel: 'Marktplätze', symbol: Store, stand: 'neu', plattformRecht: 'marktplaetze_lesen' },
      { id: 'plattform/werbung', titel: 'Retail Media', symbol: Megaphone, stand: 'neu', plattformRecht: 'marktplaetze_lesen' },
      { id: 'pakete', titel: 'Pakete & Abrechnung', symbol: Receipt, stand: 'neu', plattformRecht: 'abrechnung_lesen' },
      // mindestens der Reiter „Betriebsart“ ist für jedes Level sichtbar
      { id: 'einstellungen', titel: 'Einstellungen', symbol: Settings, stand: 'neu', plattformRecht: 'marktplaetze_lesen' },
      { id: 'anleitung', titel: 'Anleitung', symbol: BookOpen, stand: 'neu' },
    ],
  },
];

/** Reiter der Plattform-Einstellungen (ohne geöffneten Marktplatz) – auch in der Befehlspalette */
export const PLATTFORM_EINSTELLUNGEN = [
  { id: 'betreiber', titel: 'DXL als Betreiber', recht: 'einstellungen', text: 'Angaben zur DXL als Betreiber der Plattform – Absender, Bankverbindung und Steuer für die Rechnungen an die Marktplätze.' },
  { id: 'app', titel: 'DXL-App & Stripe', recht: 'einstellungen', text: 'Zugang zur App „DXL Marketplace“ und Stripe Connect der Plattform.' },
  { id: 'ki', titel: 'KI von DXL', recht: 'einstellungen', text: 'KI-Zugang der Plattform, Einkaufspreise je Modell, Verbrauch und Sperre je Marktplatz.' },
  { id: 'branding', titel: 'Branding', recht: 'einstellungen', text: 'Farben, Schrift, Logos und E-Mail-Design der DXL-Plattform – Vorgabe für Marktplätze.' },
  { id: 'rechtstexte', titel: 'Rechtstexte', recht: 'einstellungen', text: 'Vorlagen von DXL für Händlervertrag, AGB und Datenschutz der Marktplätze.' },
  { id: 'nutzer', titel: 'Nutzer & Rechte', recht: 'nutzer', text: 'Nutzer der DXL-Plattform einladen, Rechte-Level festlegen, sperren und entfernen.' },
  { id: 'email', titel: 'E-Mail-Versand', recht: 'einstellungen', text: 'Über welchen Zugang die Plattform E-Mails verschickt – Einladungen, Rechnungen und Hinweise.' },
  { id: 'backup', titel: 'Backup', recht: 'einstellungen', text: 'Daten der Plattform sichern und wiederherstellen.' },
  { id: 'betriebsart', titel: 'Betriebsart', recht: 'marktplaetze_lesen', text: 'Wie die Plattform betrieben wird: mandantenfähig, Anzahl Marktplätze, Version und Region.' },
] as const satisfies readonly { id: string; titel: string; recht: PlattformRecht; text: string }[];
export type PlattformEinstellungBereich = (typeof PLATTFORM_EINSTELLUNGEN)[number]['id'];

/** Reiter der Plattform-Einstellungen, die das Rechte-Level erlaubt */
export function plattformEinstellungenFuer(me: Me) {
  return PLATTFORM_EINSTELLUNGEN.filter((b) => !!me.plattform?.rechte.includes(b.recht));
}

const MARKTPLATZ: NavGruppe[] = [
  {
    punkte: [{ id: 'uebersicht', titel: 'Übersicht', symbol: LayoutDashboard, stand: 'neu', recht: 'view_dashboard' }],
  },
  {
    titel: 'Marktplatz',
    punkte: [
      { id: 'produkte', titel: 'Produkte', symbol: Package, stand: 'neu', recht: 'marketplace', modul: 'marktplatz' },
      { id: 'haendler', titel: 'Händler', symbol: Users, stand: 'neu', recht: 'marketplace', modul: 'marktplatz' },
      { id: 'bestellungen', titel: 'Bestellungen', symbol: ShoppingCart, stand: 'neu', recht: 'marketplace', modul: 'marktplatz' },
      { id: 'provision', titel: 'Provision', symbol: Percent, stand: 'neu', recht: 'admin', modul: 'marktplatz' },
      { id: 'auszahlungen', titel: 'Auszahlungen', symbol: Wallet, stand: 'neu', recht: 'payouts', modul: 'marktplatz' },
      { id: 'buybox', titel: 'Buy Box', symbol: Trophy, stand: 'neu', recht: 'marketplace', modul: 'marktplatz', addon: 'buybox' },
      { id: 'produktfelder', titel: 'Produktfelder', symbol: SlidersHorizontal, stand: 'neu', recht: 'marketplace', modul: 'marktplatz' },
      { id: 'channels', titel: 'Channels', symbol: Radio, stand: 'neu', recht: 'channels', modul: 'marktplatz', addon: 'channels' },
    ],
  },
  {
    titel: 'Retail Media',
    uebersicht: { id: 'werbung', recht: 'marketplace', modul: 'werbung' },
    punkte: [
      { id: 'werbekonten', titel: 'Werbekonten', symbol: Megaphone, stand: 'neu', recht: 'marketplace', modul: 'werbung' },
      { id: 'kampagnen', titel: 'Kampagnen', symbol: Target, stand: 'neu', recht: 'marketplace', modul: 'werbung' },
      { id: 'werbung/wkz', titel: 'WKZ-Buchungen', symbol: CalendarRange, stand: 'neu', recht: 'marketplace', modul: 'werbung' },
      { id: 'werbung/berichte', titel: 'Berichte', symbol: ChartColumn, stand: 'neu', recht: 'marketplace', modul: 'werbung' },
      { id: 'werbung/marken-zuordnung', titel: 'Marken-Zuordnung', symbol: Tags, stand: 'neu', recht: 'marketplace', modul: 'werbung' },
      { id: 'platzierungen', titel: 'Platzierungen', symbol: LayoutTemplate, stand: 'neu', recht: 'admin', modul: 'werbung' },
      { id: 'werbung/einbindung', titel: 'Einbindung', symbol: Code, stand: 'neu', recht: 'marketplace', modul: 'werbung' },
    ],
  },
  {
    titel: 'Ankauf',
    punkte: [
      { id: 'ankauf', titel: 'Ankaufanfragen', symbol: Handshake, stand: 'neu', recht: 'ankauf', oderRecht: 'ankauf_lesen', modul: 'ankauf' },
      { id: 'ankauf/katalog', titel: 'Uhren-Katalog', symbol: Watch, stand: 'neu', recht: 'ankauf', oderRecht: 'ankauf_lesen', modul: 'ankauf' },
    ],
  },
  {
    titel: 'ERP',
    punkte: [{ id: 'erp', titel: 'ERP-Connector', symbol: ArrowLeftRight, stand: 'neu', recht: 'erp', modul: 'erp' }],
  },
  {
    titel: 'Verwaltung',
    punkte: [
      { id: 'nutzer', titel: 'Nutzer', symbol: UserCog, stand: 'neu', recht: 'users' },
      { id: 'shop', titel: 'Marktplatz-Shop', symbol: ShoppingBag, stand: 'neu', recht: 'admin', modul: 'marktplatz' },
      { id: 'einstellungen', titel: 'Einstellungen', symbol: Settings, stand: 'neu', recht: 'admin' },
      { id: 'paket', titel: 'Paket & Abrechnung', symbol: Receipt, stand: 'neu', recht: 'admin' },
      { id: 'rechnungen', titel: 'Rechnungen', symbol: FileText, stand: 'neu', recht: 'admin' },
      { id: 'anleitung', titel: 'Anleitung', symbol: BookOpen, stand: 'neu' },
    ],
  },
];

export function navigationFuer(me: Me): NavGruppe[] {
  if (!me.mandant) {
    const rechte = new Set(me.plattform?.rechte ?? []);
    return PLATTFORM.map((g) => ({ ...g, punkte: g.punkte.filter((p) => !p.plattformRecht || rechte.has(p.plattformRecht)) })).filter((g) => g.punkte.length > 0);
  }
  const module = new Set(me.mandant.module);
  const addons = new Set<string>(me.mandant.addons);
  const erlaubt = (p: { recht?: Recht; oderRecht?: Recht; modul?: Modul }) =>
    (!p.recht || !!me.nutzer.rechte[p.recht] || (!!p.oderRecht && !!me.nutzer.rechte[p.oderRecht])) && (!p.modul || module.has(p.modul));
  return MARKTPLATZ.map((g) => ({
    ...g,
    uebersicht: g.uebersicht && erlaubt(g.uebersicht) ? g.uebersicht : undefined,
    punkte: g.punkte.filter(erlaubt).map((p) => (p.addon && !addons.has(p.addon) ? { ...p, gesperrt: true } : p)),
  })).filter((g) => g.punkte.length > 0);
}

export function navPunkt(me: Me, id: string): NavPunkt | undefined {
  return navigationFuer(me).flatMap((g) => g.punkte).find((p) => p.id === id);
}

export function navZiel(p: Pick<NavPunkt, 'id'>): { to: string } {
  return { to: `/${p.id}` };
}

export function initialen(name: string): string {
  const teile = name.trim().split(/\s+/);
  return ((teile[0]?.[0] ?? '') + (teile.length > 1 ? teile.at(-1)![0] : (teile[0]?.[1] ?? ''))).toUpperCase();
}
